<script>
  import { format } from "date-fns";
  import { filtersConfigStore } from "@stores/filtersConfigStores.js";
  import { isValidRegex } from "../../../../../../lib/util/regex";
  import {
    DatePicker,
    DatePickerInput,
    TimePicker,
    MultiSelect,
    NumberInput,
  } from "carbon-components-svelte";
  import {
    TextInput,
    Tooltip,
    Select,
    SelectItem,
    Button,
  } from "paxray-components-svelte";
  import { filter } from "d3";

  export let titleTab = "";
  export let filterCreate;
  export let isLoadingFilter = false;
  export let dateTimeIntervalStructure = {
    startDate: format(new Date(), "dd/MM/yyyy"),
    endDate: format(new Date(), "dd/MM/yyyy"),
    startDataTime: "00:00",
    endDataTime: "00:00",
    startTime: "00:00",
    endTime: "00:00",
  };

  export let dateTimeStructure = {
    date: format(new Date(), "dd/MM/yyyy"),
    time: "00:00",
  };
  export let timeStructure = {
    hours: NaN,
    mins: NaN,
    secs: NaN,
  };

  let { startDate, endDate, startDataTime, endDataTime, startTime, endTime } =
    dateTimeIntervalStructure;
  let { date, time } = dateTimeStructure;
  let { hours, mins, secs } = timeStructure;
  let addTimePicker = endDataTime !== "00:00" || startDataTime != "00:00";
  let validEndDate = false;
  let validStartTime = true;
  let validEndTime = true;
  let openHint = false;

  const { mergeColumnDefinitions, getFilterByCurrentView } = filtersConfigStore;

  let selectedIds = [];
  let logColumns = [];
  let datatype = "";
  let columnDetails = [];
  let operatorList;
  let selectValues;
  let listOfValues = [];
  let columnLabel = "Column";

  $: ({ storeLoaded } = $filtersConfigStore);

  $: validEndDate = isInvalidDate(startDate, endDate);
  $: validStartTime = isInvalidTime(startTime);
  $: validEndTime = isInvalidTime(endTime);
  $: validTime = isInvalidTime(time);
  $: validDate = isInvalidStartDate(date);

  $: resetFilterCreate(titleTab);
  $: if (storeLoaded && titleTab) {
    columnDetails = getColumnDetails(titleTab);
    columnLabel = titleTab === "KPI Filter" ? "KPI" : "Column";
    logColumns = Object.entries(columnDetails).map(([key, value]) => ({
      key,
      title: value.title,
    }));
  }

  $: modCol = filterCreate.col;

  $: setOperatorList(modCol);
  $: setTimeValue(startTime, endTime);
  $: setDateTimeIntervalValue(startDate, endDate, startDataTime, endDataTime);
  $: setDateTimeValue(date, time);
  $: setSecondsValue(hours, mins, secs);
  $: setSelectValues(modCol);

  $: comparison = filterCreate.comparison;

  $: resetValue(comparison);

  $: setDataTypeinFilterCreate(filterCreate.comparison);

  $: isInvalidRegex =
    filterCreate.comparison === "regex"
      ? !isValidRegex(filterCreate.value)
      : false;

  $: setValuesFromMultiSelect(selectedIds);

  $: if ($isLoadingFilter) {
    setSelectedIdsFromValues(filterCreate.value);
  }

  function resetValue(comparison) {
    const isMultiselect = comparison === "in" || comparison === "notin";
    if (isMultiselect && !Array.isArray(filterCreate.value))
      filterCreate.value = "";
    if (!isMultiselect && Array.isArray(filterCreate.value)) selectedIds = [];
  }

  function setSecondsValue(hours, mins, secs) {
    if ($isLoadingFilter) return;
    if (filterCreate.datatype !== "duration") return;
    filterCreate.value = "";
    if (hours === null) hours = NaN;
    if (mins === null) mins = NaN;
    if (secs === null) secs = NaN;
    if (isNaN(hours) && isNaN(mins) && isNaN(secs)) {
      filterCreate.value = "";
      return;
    }
    const validHours = isNaN(hours) ? 0 : parseInt(hours);
    const validMins = isNaN(mins) ? 0 : parseInt(mins);
    const validSecs = isNaN(secs) ? 0 : parseInt(secs);
    const totalSeconds = validHours * 3600 + validMins * 60 + validSecs;
    filterCreate.value = totalSeconds;
  }

  function setTimeValue(startTime, endTime) {
    if ($isLoadingFilter) return;
    if (filterCreate.datatype !== "timeInterval") return;
    filterCreate.value = "";
    if (!isInvalidTime(startTime) && !isInvalidTime(endTime)) {
      filterCreate.value = [startTime, endTime];
    }
  }

  function setDateTimeValue(date, time) {
    if ($isLoadingFilter) return;
    if (
      filterCreate.datatype !== "datetime" &&
      filterCreate.datatype !== "date" &&
      filterCreate.datatype !== "time"
    )
      return;
    filterCreate.value = "";
    if (filterCreate.datatype === "date" && !isInvalidStartDate(date)) {
      filterCreate.value = date.split("/").reverse().join("-");
      return;
    }
    if (filterCreate.datatype === "time" && !isInvalidTime(time)) {
      filterCreate.value = time;
      return;
    }
    if (!isInvalidStartDate(date) && !isInvalidTime(time)) {
      filterCreate.value = `${date.split("/").reverse().join("-")}T${time}`;
    }
  }

  function setDateTimeIntervalValue(
    startDate,
    endDate,
    startDataTime,
    endDataTime,
  ) {
    if ($isLoadingFilter) return;
    if (
      filterCreate.datatype !== "datetimeInterval" &&
      filterCreate.datatype !== "datetime" &&
      filterCreate.datatype !== "date" &&
      filterCreate.datatype !== "time" &&
      filterCreate.datatype !== "dateInterval"
    )
      return;
    filterCreate.value = "";
    if (
      filterCreate.datatype === "datetime" &&
      !isInvalidStartDate(startDate) &&
      !isInvalidTime(startDataTime)
    ) {
      filterCreate.value =
        startDate.split("/").reverse().join("-") + "T" + startDataTime;
      return;
    }
    if (filterCreate.datatype === "date" && !isInvalidStartDate(startDate)) {
      filterCreate.value = startDate.split("/").reverse().join("-");
      return;
    }
    if (filterCreate.datatype === "time" && !isInvalidTime(startDataTime)) {
      filterCreate.value = startDataTime;
      return;
    }
    if (
      filterCreate.datatype === "dateInterval" &&
      !isInvalidDate(startDate, endDate)
    ) {
      filterCreate.value = [
        startDate.split("/").reverse().join("-"),
        endDate.split("/").reverse().join("-"),
      ];
      return;
    }
    if (
      !isInvalidDate(startDate, endDate) &&
      !isInvalidTime(startDataTime) &&
      !isInvalidTime(endDataTime)
    ) {
      const startFinalDateTime = `${startDate.split("/").reverse().join("-")}T${startDataTime}`;
      const endFinalDateTime = `${endDate.split("/").reverse().join("-")}T${endDataTime}`;
      filterCreate.value = [startFinalDateTime, endFinalDateTime];
    }
  }

  function resetFields() {
    if ($isLoadingFilter) return;
    operatorList = [];
    selectValues = [];
    filterCreate.datatype = "";
    filterCreate.value = "";
    filterCreate.comparison = "";
    endTime = "00:00";
    startTime = "00:00";
    selectedIds = [];
    date = format(new Date(), "dd/MM/yyyy");
    time = "00:00";
  }

  function setOperatorList(col) {
    if (!$isLoadingFilter) resetFields();
    if (col === "") {
      filterCreate.comparison = "";
      return;
    }
    const { dataTypesWithTitles, filterColumnDefinitions } =
      $filtersConfigStore;
    const originalDatatype = filterColumnDefinitions[col]?.datatype;
    datatype =
      filterCreate.datatype === originalDatatype
        ? filterCreate.datatype
        : columnDetails[col]?.datatype ||
          Object.values(columnDetails).find((colDet) => {
            return colDet.columns && colDet.columns === col;
          }).datatype;
    if (Array.isArray(datatype)) {
      operatorList = dataTypesWithTitles[datatype[0]];
      operatorList = [...operatorList, ...dataTypesWithTitles[datatype[1]]];
    } else {
      operatorList = dataTypesWithTitles[datatype];
      filterCreate.datatype = datatype;
    }
    if (!operatorList || operatorList.length === 0) {
      filterCreate.comparison = "";
      return;
    }
    if (filterCreate.comparison == "" && operatorList.length > 0) {
      filterCreate.comparison = operatorList[0].operator;
    }
  }

  function setSelectedIdsFromValues(values) {
    const valuesWithID = columnDetails[filterCreate.col]?.valuesWithID || false;
    if (valuesWithID) {
      selectedIds = values;
    } else {
      const multiSelectValues = getMultiSelectValues(filterCreate.col);
      multiSelectValues.forEach((value) => {
        if (values.includes(value.text)) {
          selectedIds.push(value.id);
        }
      });
    }
  }

  function setValuesFromMultiSelect(selectedIds) {
    if ($isLoadingFilter) return;
    const valuesWithID = columnDetails[filterCreate.col]?.valuesWithID || false;

    if (valuesWithID) {
      filterCreate.value = selectedIds;
      return;
    }
    const listOfValues = getMultiSelectValues(filterCreate.col);
    if (filterCreate.datatype !== "multiSelect") return;
    filterCreate.value = listOfValues
      .filter((value) => selectedIds.includes(value.id))
      .map((value) => value.text);
  }

  function setDataTypeinFilterCreate(comparison) {
    if ($isLoadingFilter) return;
    if (!Array.isArray(datatype) || !comparison) return;
    const { dataTypesWithTitles } = $filtersConfigStore;
    const selectedOperator = comparison;

    const correspondDataType = Object.entries(dataTypesWithTitles).find(
      ([key, value]) =>
        value.find(
          (op) => op.operator === selectedOperator && datatype.includes(key),
        ),
    )[0];
    filterCreate.datatype = correspondDataType;
  }

  function setSelectValues(col) {
    if (filterCreate.datatype !== "select") return;
    const { filterColumnDefinitions } = $filtersConfigStore;
    const values = filterColumnDefinitions[col]?.values || [];
    if (values.length > 0) {
      selectValues = values.map((value, index) => {
        return { id: index, text: value };
      });
    } else {
      selectValues = [];
    }
  }

  function resetFilterCreate(titleTab) {
    if ($isLoadingFilter) return;
    filterCreate.col = "";
    operatorList = [];
    filterCreate.comparison = "";
    filterCreate.datatype = "";
    filterCreate.value = "";
  }

  function getColumnDetails(titleTab) {
    const {
      filterDefinitions,
      filterColumnDefinitions,
      currentView,
      filterAllowedOperators,
      filterOperators,
      tab,
      views,
    } = $filtersConfigStore;
    const filtersByView = getFilterByCurrentView(
      views,
      filterDefinitions,
      filterColumnDefinitions,
      filterAllowedOperators,
      filterOperators,
      currentView,
      tab,
    );

    let filterKey =
      filtersByView?.find((filter) => filter.title === titleTab)?.filterKey ||
      "";

    return mergeColumnDefinitions(
      filterDefinitions?.[filterKey] || [],
      filterColumnDefinitions || [],
    );
  }

  function hasValues(col) {
    const values = columnDetails[col]?.values || [];
    if (Object.keys(values).length > 0) {
      listOfValues = Object.values(values);
    } else {
      listOfValues = [];
    }
    return Object.keys(values).length > 0;
  }

  function getMultiSelectValues(col) {
    const values = columnDetails[col]?.values || [];
    if (!values) return [];
    const valuesWithID = columnDetails[col]?.valuesWithID || false;
    if (valuesWithID) {
      return values.map((value) => {
        return { id: value.id, text: value.title };
      });
    }
    return values.map((value, index) => {
      return { id: index, text: value };
    });
  }

  function handleTimeFilter() {
    addTimePicker = !addTimePicker;
    if (!addTimePicker) {
      startDataTime = "00:00";
      endDataTime = "00:00";
    }
  }

  function isInvalidDate(startDate, endDate) {
    const start = new Date(startDate.split("/").reverse().join("-"));
    const end = new Date(endDate.split("/").reverse().join("-"));

    const startYear = startDate.split("/").map(Number)[2];
    const endYear = startDate.split("/").map(Number)[2];

    return (
      start > end ||
      startYear < 1680 ||
      endYear < 1680 ||
      startYear > 2260 ||
      endYear > 2260
    );
  }

  function isInvalidStartDate(startDate) {
    const start = new Date(startDate.split("/").reverse().join("-"));
    const startYear = startDate.split("/").map(Number)[2];
    return startYear < 1680 || startYear > 2260;
  }

  function isInvalidTime(time) {
    const pattern = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
    return !pattern.test(time) && time !== "";
  }

  function setSelectedValues() {
    const values = Object.entries(getMultiSelectValues(filterCreate.col))
      .filter(([key, value]) => selectedIds.includes(value.id))
      .map(([key, value]) => value.text);
    return '"' + values.join('" , "') + '"';
  }

  function handle7days() {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    startDate = format(sevenDaysAgo, "dd/MM/yyyy");
    endDate = format(today, "dd/MM/yyyy");
  }

  function handle30days() {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    startDate = format(thirtyDaysAgo, "dd/MM/yyyy");
    endDate = format(today, "dd/MM/yyyy");
  }
</script>

<div class="filter_wrapper">
  <div>
    <Select bind:selected={filterCreate.col} labelText={columnLabel}>
      <SelectItem value="" text="" />
      {#each logColumns as { key, title }}
        <SelectItem value={key} text={title}></SelectItem>
      {/each}
    </Select>
    {#if columnDetails[filterCreate.col]?.hint}
      <div class="hint_wrapper">
        <Tooltip bind:openHint align="start" class="hint" direction="right">
          {columnDetails[filterCreate.col].hint}
        </Tooltip>
      </div>
    {/if}
  </div>
  {#if operatorList && operatorList.length > 0}
    <Select bind:selected={filterCreate.comparison} labelText="Comparison">
      {#each operatorList as { operator, title }}
        <SelectItem value={operator} text={title}></SelectItem>
      {/each}
    </Select>
  {/if}
  {#key `${filterCreate.col}`}
    {#if filterCreate.datatype === "number"}
      <NumberInput
        bind:value={filterCreate.value}
        hideSteppers
        label="Number"
      />
    {:else if filterCreate.datatype === "string"}
      <TextInput
        bind:value={filterCreate.value}
        labelText={columnDetails[filterCreate.col].title}
        invalid={isInvalidRegex}
        invalidText="Invalid Regex"
        list={filterCreate.col}
        placeholder={hasValues(filterCreate.col)
          ? `All ${columnDetails[filterCreate.col].title}s`
          : ""}
      />
      {#if hasValues(filterCreate.col)}
        <datalist id={filterCreate.col}>
          {#each listOfValues.sort() as value}
            <option {value} />
          {/each}
        </datalist>
      {/if}
    {:else if filterCreate.datatype === "multiSelect"}
      <div class="multiselect">
        <MultiSelect
          class="multiselect-values"
          items={getMultiSelectValues(filterCreate.col)}
          label={selectedIds.length > 0
            ? setSelectedValues()
            : "Select " + columnDetails[filterCreate.col].title + "..."}
          titleText={columnDetails[filterCreate.col].title}
          bind:selectedIds
        ></MultiSelect>
      </div>
    {:else if filterCreate.datatype === "timeInterval"}
      <div class="time_interval">
        <TimePicker
          labelText="Start Time"
          placeholder="hh:mm"
          bind:value={startTime}
          invalid={validStartTime}
        />
        <TimePicker
          labelText="End Time"
          placeholder="hh:mm"
          bind:value={endTime}
          invalid={validEndTime}
          class="time_picker"
        />
      </div>
    {:else if filterCreate.datatype === "datetimeInterval"}
      <div class="datetime_interval">
        <div>
          <DatePicker
            datePickerType="single"
            bind:value={startDate}
            dateFormat="d/m/Y"
            short
          >
            <DatePickerInput
              labelText="Start Date"
              placeholder="dd/mm/yyyy"
              invalidText="The end date cannot be before the start date."
              invalid={validEndDate}
            />
            {#if addTimePicker}
              <TimePicker
                labelText="Start Time"
                placeholder="hh:mm"
                bind:value={startDataTime}
                invalid={validStartTime}
              />
            {/if}
          </DatePicker>

          <DatePicker
            datePickerType="single"
            bind:value={endDate}
            dateFormat="d/m/Y"
            short
          >
            <DatePickerInput
              labelText="End Date"
              placeholder="dd/mm/yyyy"
              invalidText="Please select a valid time range. "
              invalid={validEndDate}
            />
            {#if addTimePicker}
              <TimePicker
                labelText="End Time"
                placeholder="hh:mm"
                bind:value={endDataTime}
                invalid={validEndTime}
              />
            {/if}
          </DatePicker>
        </div>
        <div class="button_time_picker">
          <Button
            text="{addTimePicker ? 'Remove' : 'Add'} Time Filter"
            kind="secondary"
            on:click={handleTimeFilter}
          />
        </div>
      </div>
      <div class="button_wrapper">
        <Button text="Last 7 Days" kind="secondary" on:click={handle7days} />
        <Button text="Last 30 Days" kind="secondary" on:click={handle30days} />
      </div>
    {:else if filterCreate.datatype === "dateInterval"}
      <div class="date_interval">
        <DatePicker
          datePickerType="single"
          bind:value={startDate}
          dateFormat="d/m/Y"
          short
        >
          <DatePickerInput
            labelText="Start Date"
            placeholder="dd/mm/yyyy"
            invalidText="The end date cannot be before the start date."
            invalid={validEndDate}
          />
        </DatePicker>

        <DatePicker
          datePickerType="single"
          bind:value={endDate}
          dateFormat="d/m/Y"
          short
        >
          <DatePickerInput
            labelText="End Date"
            placeholder="dd/mm/yyyy"
            invalidText="Please select a valid time range. "
            invalid={validEndDate}
          />
        </DatePicker>
      </div>
      <div class="button_wrapper">
        <Button text="Last 7 Days" kind="secondary" on:click={handle7days} />
        <Button text="Last 30 Days" kind="secondary" on:click={handle30days} />
      </div>
    {:else if filterCreate.datatype === "datetime"}
      <div class="datetime_interval">
        <DatePicker
          datePickerType="single"
          bind:value={date}
          dateFormat="d/m/Y"
          short
        >
          <DatePickerInput
            labelText="Date"
            placeholder="dd/mm/yyyy"
            invalidText="The date should be between 1680 and 2260."
            invalid={validDate}
          />
          {#if addTimePicker}
            <TimePicker
              labelText="Time"
              placeholder="hh:mm"
              bind:value={time}
              invalid={validTime}
            />
          {/if}
        </DatePicker>
        <div class="button_time_picker datetime">
        <Button
            text="{addTimePicker ? 'Remove' : 'Add'} Time Filter"
            kind="secondary"
            on:click={handleTimeFilter}
          />
        </div>
      </div>
    {:else if filterCreate.datatype === "date"}
      <div class="date">
        <DatePicker
          datePickerType="single"
          bind:value={date}
          dateFormat="d/m/Y"
          short
        >
          <DatePickerInput
            labelText="Date"
            placeholder="dd/mm/yyyy"
            invalidText="The date should be between 1680 and 2260."
            invalid={validDate}
          />
        </DatePicker>
      </div>
    {:else if filterCreate.datatype === "time"}
      <div class="time">
        <TimePicker
          labelText="Time"
          placeholder="hh:mm"
          bind:value={time}
          invalid={validTime}
        />
      </div>
    {:else if filterCreate.datatype === "duration"}
      <div class="time_wrapper">
        <NumberInput
          hideSteppers
          light
          label="Hours"
          bind:value={hours}
          allowEmpty
        />
        <NumberInput
          hideSteppers
          light
          label="Minutes"
          bind:value={mins}
          allowEmpty
        />
        <NumberInput
          hideSteppers
          light
          label="Seconds"
          bind:value={secs}
          allowEmpty
        />
      </div>
    {:else if filterCreate.datatype === "select"}
      <Select
        bind:selected={filterCreate.value}
        labelText="Select {columnDetails[filterCreate.col].title}"
      >
        <SelectItem value="" text="" />
        {#if selectValues && selectValues.length > 0}
          {#each selectValues as { id, text }}
            <SelectItem value={text} {text}></SelectItem>
          {/each}
        {:else}
          <SelectItem value="" text="No values available" disabled></SelectItem>
        {/if}
      </Select>
    {/if}
  {/key}
</div>

<style>
  .filter_wrapper {
    display: grid;
    grid-template-columns: 3fr 1fr 3fr;
    gap: 1rem;
  }

  :global(.multiselect .bx--list-box__label) {
    max-width: 28rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 3rem;
  }

  .button_time_picker {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .time_interval {
    width: auto;
    height: auto;
  }

  :global(.time_interval .bx--form-item) {
    align-items: center;
    display: inline-block;
    margin-right: 1.5rem;
    margin-left: 1rem;
  }

  .datetime_interval {
    display: flex;
    gap: 1.5rem;
    justify-content: space-between;
    margin: 0 1.5rem;
  }

  .date_interval,
  .date,
  .time {
    margin-left: 1.5rem;
  }

  .time_wrapper {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    position: relative;
  }

  .button_wrapper {
    grid-column: 3;
    display: flex;
    gap: 1rem;
    justify-content: flex-start;
    margin: 1rem 0;
    margin-left: 1.5rem;
  }

  :global(
      .datetime_interval
        .bx--date-picker.bx--date-picker--single
        .bx--date-picker__input
    ) {
    width: 16rem;
  }

  .button_time_picker.datetime {
    align-items: end;
  }

  .hint_wrapper {
    margin-top: 0.5rem;
  }
</style>
